import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { SunMedium, Wrench, Activity, ArrowUpRight } from "lucide-react";
import CornerMarks from "@/components/site/CornerMarks";

const SERVICES = [
  {
    id: "solar",
    icon: SunMedium,
    title: "Solar EPC Solutions",
    description:
      "Grid-tied, hybrid, off-grid, and battery backup systems for homes, businesses, and institutions.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/4c76c86a-f07c-4532-8d48-6ac9bac60dfc/images/0df023cf6459111bb2738c2e3091f5dbabc02e1d91fdf23dc58bcaae29806490.png",
    tag: "01 · ENERGY",
    spec: "Design · Supply · Install",
    accent: "blue",
    prefill:
      "I'd like to discuss a Solar EPC project (grid-tied / hybrid / off-grid). Site type, location and target capacity: ",
  },
  {
    id: "mep",
    icon: Wrench,
    title: "MEP Engineering",
    description:
      "HVAC, electrical, plumbing, and building services engineering for developers and real estate projects.",
    image: "/tafra-mep-ducts.webp",
    tag: "02 · SYSTEMS",
    spec: "Design · Build · Commission",
    accent: "crimson",
    prefill:
      "I'd like to discuss MEP engineering for a building/real-estate project. Scope, location and timeline: ",
  },
  {
    id: "optimization",
    icon: Activity,
    title: "Energy Optimization",
    description:
      "Energy audits, consumption analysis, cost-reduction strategies, and smart monitoring solutions.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/4c76c86a-f07c-4532-8d48-6ac9bac60dfc/images/0f0f1304c749b37d9bef3773e865ed974b90df9308ee96707cd5749501ccc04e.png",
    tag: "03 · INSIGHT",
    spec: "Audit · Monitor · Reduce",
    accent: "blue",
    prefill:
      "I'd like an energy audit / optimization assessment for our facility. Building type, monthly consumption and pain points: ",
  },
];

const accentMap = {
  blue: { hex: "var(--accent-blue)", rgba: "var(--accent-blue-glow)" },
  crimson: { hex: "var(--accent-crimson-hover)", rgba: "var(--accent-crimson-soft)" },
};

const ServiceCard = ({ data, index, onOpen }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 200, damping: 22, mass: 0.5 });
  const sy = useSpring(my, { stiffness: 200, damping: 22, mass: 0.5 });
  const rotX = useTransform(sy, [-0.5, 0.5], [6, -6]);
  const rotY = useTransform(sx, [-0.5, 0.5], [-6, 6]);

  // For the CSS sheen following the cursor (set as CSS custom props)
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    mx.set(nx);
    my.set(ny);
    el.style.setProperty("--mx", `${((nx + 0.5) * 100).toFixed(2)}%`);
    el.style.setProperty("--my", `${((ny + 0.5) * 100).toFixed(2)}%`);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  const Icon = data.icon;
  const acc = accentMap[data.accent];

  return (
    <motion.article
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ y: 40, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.2, 0.7, 0.2, 1] }}
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        boxShadow: "0 4px 30px rgba(0,0,0,0.10)",
        rotateX: rotX,
        rotateY: rotY,
        transformPerspective: 1200,
        transformStyle: "preserve-3d",
        willChange: "transform",
      }}
      whileHover={{ y: -6 }}
      data-testid={`service-card-${index}`}
      className="group relative overflow-hidden btn-sharp service-card"
    >
      <button
        type="button"
        onClick={() => onOpen(data.prefill)}
        data-testid={`service-card-cta-${index}`}
        aria-label={`Enquire about ${data.title}`}
        className="absolute inset-0 z-20 cursor-pointer focus:outline-none"
      />

      <div className="relative h-56 overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${data.image})`,
            translateZ: 0,
          }}
          whileHover={{ scale: 1.12 }}
          transition={{ duration: 1.4, ease: [0.2, 0.7, 0.2, 1] }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, transparent 50%, var(--surface) 100%)",
          }}
        />
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <span
            className="font-display uppercase tracking-[0.25em] text-[0.6rem] font-semibold backdrop-blur-md px-2.5 py-1 border btn-sharp"
            style={{
              color: acc.hex,
              borderColor: acc.hex,
              background: "rgba(0,0,0,0.55)",
            }}
          >
            {data.tag}
          </span>
          <span
            className="w-9 h-9 flex items-center justify-center backdrop-blur-md border btn-sharp"
            style={{
              borderColor: acc.hex,
              background: "rgba(0,0,0,0.55)",
            }}
          >
            <Icon className="w-4 h-4" strokeWidth={1.6} style={{ color: acc.hex }} />
          </span>
        </div>

        {/* Scanline that sweeps on hover */}
        <span
          aria-hidden
          className="pointer-events-none absolute left-0 right-0 h-px opacity-0 group-hover:opacity-100"
          style={{
            top: "50%",
            background:
              "linear-gradient(90deg, transparent, var(--accent-blue), transparent)",
            transform: "translateY(-50%)",
            animation: "scanlineSweep 1.4s ease-in-out infinite",
          }}
        />
      </div>

      <div className="relative p-6 lg:p-7">
        <h3
          className="font-display text-xl lg:text-2xl font-medium"
          style={{ color: "var(--text)" }}
        >
          {data.title}
        </h3>
        <p
          className="mt-3 leading-relaxed text-[15px]"
          style={{ color: "var(--text-muted)" }}
        >
          {data.description}
        </p>

        <div
          className="mt-6 pt-5 border-t flex items-center justify-between"
          style={{ borderColor: "var(--border)" }}
        >
          <span
            className="font-mono-spec text-[10px] tracking-[0.2em] uppercase"
            style={{ color: "var(--text-faint)" }}
          >
            {data.spec}
          </span>
          <span
            className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-semibold"
            style={{ color: acc.hex }}
          >
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              Enquire
            </span>
            <span
              className="flex items-center justify-center w-9 h-9 border btn-sharp transition-all group-hover:translate-x-1"
              style={{ borderColor: "var(--border)" }}
            >
              <ArrowUpRight
                className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={1.6}
                style={{ color: acc.hex }}
              />
            </span>
          </span>
        </div>
      </div>

      <span
        className="pointer-events-none absolute -bottom-px left-0 h-px w-0 group-hover:w-full transition-all duration-700"
        style={{ background: acc.hex }}
      />

      {/* Cursor-following sheen */}
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background: `radial-gradient(600px circle at var(--mx, 50%) var(--my, 0%), ${acc.rgba}, transparent 40%)`,
        }}
      />

      {/* Gradient border on hover (mask trick) */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          padding: 1,
          background: `conic-gradient(from var(--angle, 0deg), ${acc.hex}, transparent 40%, ${acc.hex} 80%)`,
          WebkitMask:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          animation: "conicSpin 4s linear infinite",
        }}
      />
    </motion.article>
  );
};

const Services = ({ onOpenContact }) => {
  return (
    <section
      id="services"
      className="relative py-24 sm:py-28 lg:py-36"
      style={{ backgroundColor: "var(--bg)" }}
      data-testid="services-section"
    >
      <div className="absolute inset-0 energy-grid-bg opacity-[0.18] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-end mb-12 lg:mb-16">
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <span className="eyebrow">What we engineer</span>
            <h2
              className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-[1.05]"
              style={{ color: "var(--text)" }}
            >
              Reliable energy systems,
              <br />
              <span className="font-medium">precisely engineered.</span>
            </h2>
          </motion.div>

          <motion.p
            initial={{ y: 24, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 leading-relaxed text-base md:text-lg"
            style={{ color: "var(--text-muted)" }}
          >
            From rooftop solar arrays to full MEP build-outs and ongoing energy
            optimization — TAFRA Energy delivers integrated engineering from
            design to commissioning.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard
              key={s.id}
              data={s}
              index={i}
              onOpen={(prefill) => onOpenContact && onOpenContact(prefill)}
            />
          ))}
        </div>

        <motion.div
          initial={{ y: 16, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-16 max-w-3xl mx-auto text-center"
          data-testid="trust-statement"
        >
          <span
            className="font-mono-spec text-[10px] tracking-[0.3em] uppercase block mb-3"
            style={{ color: "var(--accent-crimson-hover)" }}
          >
            // Engineering principle
          </span>
          <p
            className="font-display text-lg md:text-xl"
            style={{ color: "var(--text-muted)" }}
          >
            Engineering reliable energy systems for homes, businesses, and real
            estate developments.
          </p>
        </motion.div>
      </div>
      <CornerMarks code="SEC—03.SERVICES" />
    </section>
  );
};

export default Services;
